import React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Handshake } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Partner logo carousel — shows confirmed partners only.
 * When empty, displays a respectful invitation (no fake logos).
 */
export function PartnerLogoCarousel({ partners = [], className }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });

  const hasPartners = partners.length > 0;

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  if (!hasPartners) {
    return (
      <div
        className={cn(
          'partners-network-empty flex flex-col items-center justify-center rounded-2xl border border-dashed border-[hsl(38_16%_82%)] bg-[hsl(42_28%_99%)] px-6 py-12 text-center',
          className,
        )}
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(43_48%_58%_/_0.25)] bg-[hsl(43_48%_58%_/_0.08)] text-[#c9a961]">
          <Handshake className="h-5 w-5" strokeWidth={1.5} aria-hidden />
        </span>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-[hsl(32_10%_38%)]">
          We are preparing our partner network. Real partner logos will appear here once confirmed.
        </p>
      </div>
    );
  }

  return (
    <div className={cn('partners-logo-carousel', className)}>
      <div className="overflow-hidden" ref={emblaRef}>
        <ul className="flex gap-6">
          {partners.map((partner) => (
            <li
              key={partner.id || partner.partner_code}
              className="flex min-w-[10rem] max-w-[12rem] shrink-0 flex-col items-center justify-center rounded-xl border border-[hsl(38_16%_82%)] bg-white px-4 py-5"
            >
              {partner.logoUrl ? (
                <img
                  src={partner.logoUrl}
                  alt={partner.name}
                  className="max-h-12 w-auto object-contain"
                  loading="lazy"
                />
              ) : (
                <span className="font-serif text-sm text-[hsl(24_14%_16%)]">{partner.name}</span>
              )}
              {partner.partner_type ? (
                <p className="mt-2 text-[10px] uppercase tracking-wider text-[hsl(32_10%_45%)]">
                  {partner.partner_type}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
      {partners.length > 3 ? (
        <div className="mt-4 flex justify-center gap-2">
          <button
            type="button"
            onClick={scrollPrev}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(38_16%_82%)] bg-white text-[hsl(32_10%_38%)] hover:border-[hsl(38_36%_42%)]"
            aria-label="Previous partners"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[hsl(38_16%_82%)] bg-white text-[hsl(32_10%_38%)] hover:border-[hsl(38_36%_42%)]"
            aria-label="Next partners"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default PartnerLogoCarousel;
